import React, { useEffect, useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTacitsBalance, TacitsBalance } from "../../api/pricing";
import styles from "./TacitsBadge.module.scss";

interface TacitsBadgeProps {
  isDarkTheme: boolean;
}

function formatNumber(n: number): string {
  return Math.round(n).toLocaleString("pt-BR");
}

const TacitsBadge: React.FC<TacitsBadgeProps> = ({ isDarkTheme }) => {
  const navigate = useNavigate();
  const gradId = useId().replace(/:/g, "");
  const [balance, setBalance] = useState<TacitsBalance>({
    allocated: 0,
    manual_credits: 0,
    used: 0,
    remaining: 0,
    plan: "—",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getTacitsBalance()
      .then((data) => {
        if (!cancelled) {
          setBalance(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div
        className={`${styles.skeleton} ${!isDarkTheme ? styles.lightTheme : ""}`}
      />
    );
  }

  const tooltipText = `${formatNumber(balance.remaining)} de ${formatNumber(balance.allocated)} tacits`;

  return (
    <div
      className={`${styles.badge} ${!isDarkTheme ? styles.lightTheme : ""}`}
      onClick={() => navigate("/settings/tacits")}
      title={tooltipText}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && navigate("/settings/tacits")}
    >
      <svg
        className={styles.coin}
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient
            id={`coinGrad_${gradId}`}
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor="#f7d84a" />
            <stop offset="50%" stopColor="#e8a800" />
            <stop offset="100%" stopColor="#b87800" />
          </linearGradient>
          <radialGradient
            id={`coinHighlight_${gradId}`}
            cx="35%"
            cy="30%"
            r="45%"
          >
            <stop offset="0%" stopColor="rgba(255,255,255,0.5)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </radialGradient>
        </defs>
        <circle
          cx="12"
          cy="12"
          r="11"
          fill={`url(#coinGrad_${gradId})`}
          stroke="#8a5a00"
          strokeWidth="0.8"
        />
        <circle cx="12" cy="12" r="11" fill={`url(#coinHighlight_${gradId})`} />
        <text
          x="12"
          y="16.5"
          textAnchor="middle"
          fontSize="11"
          fontWeight="700"
          fill="#5a3400"
          fontFamily="Arial, sans-serif"
        >
          T
        </text>
      </svg>
      <span className={styles.amount}>{formatNumber(balance.remaining)}</span>
      <span className={styles.tooltip}>{tooltipText}</span>
    </div>
  );
};

export default TacitsBadge;
